/* 15c2a311: pure view model for the «Мой налог» cabinet UI — status
   normalization, RU labels, badge classes, phone/code validation and the
   connected-card view builder. No React, no fetch: mirrored 1:1 by
   tests/nalogView.test.ts on the node test runner (same pattern as
   productOptionsView / ordersPagerView). */

export type NalogConnectionStatus = 'active' | 'disconnected' | 'error';
export type NalogReceiptStatus = 'pending' | 'succeeded' | 'canceled' | 'failed';

/** API lowercases statuses; numbers stay accepted defensively (enum drift). */
export function normalizeNalogConnectionStatus(status: string | number): NalogConnectionStatus {
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s === 'active') return 'active';
    if (s === 'disconnected') return 'disconnected';
    return 'error';
  }
  // 0 Active, 1 Disconnected, 2 Error — ProviderNalogConnectionStatus order.
  return status === 0 ? 'active' : status === 1 ? 'disconnected' : 'error';
}

export function normalizeNalogReceiptStatus(status: string | number): NalogReceiptStatus {
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s === 'pending') return 'pending';
    if (s === 'succeeded') return 'succeeded';
    if (s === 'canceled') return 'canceled';
    return 'failed';
  }
  // 0 Pending, 1 Succeeded, 2 Failed, 3 Canceled — NalogReceiptStatus order.
  return status === 0 ? 'pending' : status === 1 ? 'succeeded' : status === 3 ? 'canceled' : 'failed';
}

export const NALOG_CONNECTION_LABELS: Record<NalogConnectionStatus, string> = {
  active: 'Подключён',
  disconnected: 'Отключён',
  error: 'Ошибка',
};

export const NALOG_RECEIPT_LABELS: Record<NalogReceiptStatus, string> = {
  pending: 'Формируется',
  succeeded: 'Чек готов',
  canceled: 'Аннулирован',
  failed: 'Ошибка',
};

export function nalogConnectionBadgeClass(status: NalogConnectionStatus): string {
  switch (status) {
    case 'active': return 'badge badge-success';
    case 'disconnected': return 'badge badge-neutral';
    default: return 'badge badge-danger';
  }
}

export function nalogReceiptBadgeClass(status: NalogReceiptStatus): string {
  switch (status) {
    case 'succeeded': return 'badge badge-success';
    case 'canceled': return 'badge badge-neutral';
    case 'failed': return 'badge badge-danger';
    default: return 'badge badge-warning';
  }
}

/* ── Wizard input validation (mirrors backend rules exactly) ── */

/**
 * Russian mobile only, exactly as ProviderNalogService.NormalizePhone:
 * 10 digits starting with 9, or 11 digits starting with 7/8 + 9…
 * (domestic trunk). Everything non-digit is ignored on the wire, so
 * +7 (999) 123-45-67 style input is valid.
 */
export function isValidNalogPhone(input: string): boolean {
  let digits = (input.match(/\d/g) ?? []).join('');
  if (digits.length === 11 && (digits[0] === '8' || digits[0] === '7')) {
    digits = digits.slice(1);
  }
  return digits.length === 10 && digits[0] === '9';
}

/** Formats a normalized +7… phone as +7 999 123-45-67 for display. */
export function formatNalogPhone(phone: string): string {
  const digits = (phone.match(/\d/g) ?? []).join('');
  if (digits.length !== 11) return phone;
  return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
}

/** 4–8 digits — the same envelope VerifySmsCodeAsync enforces. */
export function isValidNalogSmsCode(code: string): boolean {
  return /^\d{4,8}$/.test(code.trim());
}

/* ── Connected-card view builder ── */

export interface NalogCardView {
  status: NalogConnectionStatus;
  statusLabel: string;
  badgeClass: string;
  /** Human lines for the card body: «ФИО», «ИНН», phone, dates. */
  lines: { label: string; value: string }[];
  /** Only the error status surfaces LastError (never tokens — API is token-free). */
  problemText: string | null;
  /** Reconnect offered unless already active; disconnect only when active. */
  canReconnect: boolean;
  canDisconnect: boolean;
}

export function buildNalogCardView(
  connection: {
    phone: string;
    inn?: string | null;
    displayName?: string | null;
    status: string | number;
    connectedAtUtc: string;
    disconnectedAtUtc?: string | null;
    lastError?: string | null;
  },
  formatDate: (iso: string) => string,
): NalogCardView {
  const status = normalizeNalogConnectionStatus(connection.status);
  const lines: { label: string; value: string }[] = [];
  if (connection.displayName) lines.push({ label: 'Плательщик', value: connection.displayName });
  if (connection.inn) lines.push({ label: 'ИНН', value: connection.inn });
  lines.push({ label: 'Телефон', value: formatNalogPhone(connection.phone) });
  if (status === 'disconnected' && connection.disconnectedAtUtc) {
    lines.push({ label: 'Отключён', value: formatDate(connection.disconnectedAtUtc) });
  } else {
    lines.push({ label: 'Подключён', value: formatDate(connection.connectedAtUtc) });
  }
  return {
    status,
    statusLabel: NALOG_CONNECTION_LABELS[status],
    badgeClass: nalogConnectionBadgeClass(status),
    lines,
    problemText: status === 'error' && connection.lastError ? connection.lastError : null,
    canReconnect: status !== 'active',
    canDisconnect: status === 'active',
  };
}

/* ── Receipt-row view builder ── */

export interface NalogReceiptView {
  status: NalogReceiptStatus;
  statusLabel: string;
  badgeClass: string;
  amountLabel: string;
  /** «Чек №…» short uuid tail — full ids only bloat the row. */
  uuidTail: string | null;
  problemText: string | null;
  cancelComment: string | null;
  /** Download (file fetch+save) makes sense once the tax service formed it. */
  canDownload: boolean;
  /** Cancel is terminal: only a formed, not-yet-canceled receipt. */
  canCancel: boolean;
}

export function buildNalogReceiptView(receipt: {
  amountRub: number;
  status: string | number;
  receiptUuid?: string | null;
  canceledAtUtc?: string | null;
  cancelComment?: string | null;
  failureReason?: string | null;
}): NalogReceiptView {
  const status = normalizeNalogReceiptStatus(receipt.status);
  const amount = Number(receipt.amountRub) || 0;
  const uuid = receipt.receiptUuid?.trim() || null;
  return {
    status,
    statusLabel: NALOG_RECEIPT_LABELS[status],
    badgeClass: nalogReceiptBadgeClass(status),
    amountLabel: `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`,
    uuidTail: uuid ? (uuid.length > 12 ? uuid.slice(-8) : uuid) : null,
    problemText: status === 'failed' && receipt.failureReason ? receipt.failureReason : null,
    cancelComment: status === 'canceled' && receipt.cancelComment ? receipt.cancelComment : null,
    canDownload: status === 'succeeded' || status === 'canceled',
    canCancel: status === 'succeeded',
  };
}
